import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';
import errorInterceptor from './interceptors/errorInterceptor';
import { IApiErrorDetail } from '../../errors/interfaces/IApiError';
import ConfigurationManager from '../../../utils/configurationManager';
import getUserLanguage from '../../../utils/languageUtils';

export const getApiErrorMessage = (
    error: AxiosError<IApiErrorDetail[]>,
    fallbackMessage?: string,
): string => {
    let errorMessage = '';

    if (error.response?.status === 400 && error.response?.data?.length > 0) {
        errorMessage = error.response?.data?.map(x => x.Error).join('; ');
    }

    return errorMessage.length === 0 && fallbackMessage
        ? fallbackMessage
        : errorMessage;
};

export const getApiErrorType = (error: AxiosError<IApiErrorDetail[]>): string =>
    error?.response?.data[0]?.Type ?? '';

export const getAuthAxiosInstance = async (): Promise<AxiosInstance> => {
    const { API_AUTH_BASE_URL, OCP_APIM_SUBSCRIPTION_KEY } =
        await ConfigurationManager.getInstance().getConfigFile();
    return getAxiosInstance(API_AUTH_BASE_URL, OCP_APIM_SUBSCRIPTION_KEY);
};

export const getNotificationAxiosInstance =
    async (): Promise<AxiosInstance> => {
        const { API_NOTIFICATION_BASE_URL, OCP_APIM_SUBSCRIPTION_KEY } =
            await ConfigurationManager.getInstance().getConfigFile();
        return getAxiosInstance(
            API_NOTIFICATION_BASE_URL,
            OCP_APIM_SUBSCRIPTION_KEY,
        );
    };

export const getLoadManagerAxiosInstance = async (): Promise<AxiosInstance> => {
    const { API_LOADMANAGER_BASE_URL, OCP_APIM_SUBSCRIPTION_KEY } =
        await ConfigurationManager.getInstance().getConfigFile();
    return getAxiosInstance(
        API_LOADMANAGER_BASE_URL,
        OCP_APIM_SUBSCRIPTION_KEY,
    );
};

export const getCarrierAxiosInstance = async (): Promise<AxiosInstance> => {
    const { API_CARRIER_BASE_URL, OCP_APIM_SUBSCRIPTION_KEY } =
        await ConfigurationManager.getInstance().getConfigFile();
    return getAxiosInstance(API_CARRIER_BASE_URL, OCP_APIM_SUBSCRIPTION_KEY);
};

export const getRoundManagerApiAxiosInstance =
    async (): Promise<AxiosInstance> => {
        const { API_ROUNDMANAGER_API_BASE_URL, OCP_APIM_SUBSCRIPTION_KEY } =
            await ConfigurationManager.getInstance().getConfigFile();
        return getAxiosInstance(
            API_ROUNDMANAGER_API_BASE_URL,
            OCP_APIM_SUBSCRIPTION_KEY,
        );
    };

const getAxiosInstance = async (
    baseUrl: string,
    ocpApimSubscriptionKey?: string,
): Promise<AxiosInstance> => {
    const token = sessionStorage.getItem('AccessToken');
    const identityToken = sessionStorage.getItem('IdentityToken');
    const userLanguage = localStorage.getItem('UserLanguageSelected');
    const language = getUserLanguage(userLanguage);
    const WebHeader = 'WEB';
    const config: AxiosRequestConfig = {
        baseURL: baseUrl,
        headers: {
            Authorization: `Bearer ${token}`,
            Identity: `${identityToken}`,
            'Ocp-Apim-Subscription-Key': `${ocpApimSubscriptionKey}`,
            'Accept-Language': language,
            'X-SmartCargas-Origin': WebHeader,
        },
    };

    const api = axios.create(config);

    api.interceptors.response.use(
        (response: AxiosResponse) => response,
        (error: AxiosError) => errorInterceptor(error),
    );

    return api;
};
